import { getPackageConfiguration } from 'legacyfrontend/api/services/packageSuppliersServices';
import Flex from 'legacyfrontend/components/Flex';
import LoadSpinner from 'legacyfrontend/components/spinner';
import DynamicIncludesRow from 'legacyfrontend/components/packages/DynamicIncludesRow';
import { DATE_KEYS, FALLBACK_IMAGE } from 'legacyfrontend/constants';
import { adultsQtyMapper } from 'legacyfrontend/helpers/julia-mappers';
import useInformation from 'legacyfrontend/hooks/useInformation';
import { formatWithMomentFormat, utcToLocal } from 'legacyfrontend/utils/dateUtils';
import getFinalPrice from 'legacyfrontend/utils/get-final-price';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ContactIconsGroup } from '../../components/ui';
import store from 'storeClient';
import ContactFormModal from '../../components/ContactFormModal';
import ShareButton from '../../components/ui/ShareButton';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { selectPaisId } from 'legacyfrontend/redux/selectors/application-selectors';
import { Seo } from 'core/components/seo';
import { useAppName } from 'legacyfrontend/hooks/useSafeState';
import ImageGallery from './components/ImageGallery';
import PackageHotelsInfo from './components/PackageHotelsInfo';
import PaymentSummaryRow from './components/PaymentSummaryRow';
import PackageDetailFlightsInfo from './components/FlightsInfo';
import { saveSearchParameters, mergeSearchParametersWithStored } from 'legacyfrontend/helpers/searchParametersStorage';
import 'styles/components/paquete-detalle.css';

const getAdultsQtyText = (adultsQty) => {
  const mappedQty = adultsQtyMapper(adultsQty);
  if (mappedQty === 0) return '-';
  if (mappedQty === 1) {
    return '1 adulto';
  } else {
    return `${mappedQty} adultos`;
  }
};

const PackageDetail = (props) => {
  const { t } = useTranslation('layout');
  const { background: mbrBackground } = useColors();
  const location = useLocation();
  const history = useHistory();
  const paisId = useSelector(selectPaisId);
  const appName = useAppName();
  const [configuration, setConfiguration] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const seoData = useMemo(() => {
    if (!configuration?.package) {
      return {
        title: `Detalle del Paquete | ${appName}`,
        description: `Consultá los detalles completos de este paquete turístico en ${appName}. Hoteles, vuelos, precios y más.`,
      };
    }

    const pkg = configuration.package;
    const destCity = pkg.packageDestinations?.[0]?.destinationCity?.nombreCiudad;
    const originCity = pkg.packageOrigins?.[0]?.originExporta?.nombreCiudad;
    const totalPrice = configuration.totalManager?.total;
    const currency = pkg.currencyCode || 'USD';
    const priceStr = totalPrice
      ? getFinalPrice({ currencyCode: currency, total: totalPrice })
      : '';

    const title = pkg.title
      ? `${pkg.title} | ${appName}`
      : destCity
        ? `Paquete a ${destCity} | ${appName}`
        : `Detalle del Paquete | ${appName}`;

    let description = `Reservá este paquete turístico en ${appName}.`;
    if (destCity && originCity) {
      description = `Paquete turístico de ${originCity} a ${destCity}`;
      if (priceStr) {
        description += ` por ${priceStr}`;
      }
      description += `. Reservá en ${appName} con los mejores precios y financiación.`;
    } else if (destCity) {
      description = `Paquete turístico a ${destCity}. Viajá con los mejores precios en ${appName}.`;
    }

    return { title, description };
  }, [configuration, appName]);

  const productJsonLd = useMemo(() => {
    if (!configuration?.package) return null;

    const pkg = configuration.package;
    const totalPrice = configuration.totalManager?.total;
    const currency = pkg.currencyCode || 'USD';
    const imageSrc =
      pkg.images?.[0]?.url ||
      pkg.packageDestinations?.[0]?.destinationCity?.imageUrl;

    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: pkg.title || 'Paquete turístico',
      description: seoData.description,
      ...(imageSrc ? { image: imageSrc } : {}),
    };

    if (
      totalPrice !== null &&
      totalPrice !== undefined &&
      Number.isFinite(Number(totalPrice))
    ) {
      ld.offers = {
        '@type': 'Offer',
        price: String(totalPrice),
        priceCurrency: currency,
        availability: 'https://schema.org/InStock',
      };
    }

    if (typeof window !== 'undefined') {
      ld.url = window.location.href.split('#')[0];
    }

    return ld;
  }, [configuration, seoData]);

  useEffect(() => {
    const fetchConfiguration = async () => {
      try {
        setIsLoading(true);
        const data = await getPackageConfiguration(props.match.params.id);
        setConfiguration(data);
        setError(null);
      } catch (err) {
        setError(err);
        history.replace('/');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfiguration();
  }, [props.match.params.id, history]);

  const { web } = store.getState().applicationState;
  const url = web === 'localhost' ? 'http://localhost:3000' : `https://${web}`;
  const urlComplete = `${url}/packshare/${props.match.params.id}`;
  const urlIntranet = `${url}/paquete/${props.match.params.id}`;

  const images = useMemo(() => configuration?.package?.images, [configuration?.package]);

  const [consultaPackage, setConsultaPackage] = useState();

  const packageRefNumber = configuration?.package?.nroRef || '';

  const sharePackage = useCallback(async () => {
    navigator.clipboard.writeText(urlComplete);
    alert(t('layout:common.link_copied'));
  }, [urlComplete]);

  const information = useInformation(
    configuration?.package?.provider_id === 1
      ? configuration?.package?.packageDescriptions?.[0]?.description
      : undefined
  );

  useEffect(() => {
    isClient &&
      window.scrollTo({
        top: 0,
      });

    if (isClient && configuration?.package) {
      const mergedParams = mergeSearchParametersWithStored(
        props.location.search,
        configuration.package,
        null,
        false
      );
      saveSearchParameters(mergedParams);
    }
  }, [configuration?.package]);

  return (
    <>
      <Seo {...seoData} jsonLd={productJsonLd} />
      {!!configuration && !isLoading ? (
        <Flex className='package-detail-container p-4 gap-8' vertical>
          {configuration.package.title && (
            <h1 className='package-detail__title' style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.5rem 0', lineHeight: 1.3 }}>
              {configuration.package.title}
            </h1>
          )}
          <Flex className='boxes-wrapper'>
            <Flex className='boxes-wrapper package-information' vertical>
              <ImageGallery
                items={
                  configuration.package.provider_id === 1
                    ? [configuration?.package?.packageDestinations?.[0]?.destinationCity?.imageUrl]
                    : (images ?? FALLBACK_IMAGE)
                }
                title={configuration.package.title}
                shareButton={
                  <ShareButton 
                    url={urlComplete}
                    title="Compartir paquete"
                    variant="package"
                  />
                }
              />
              <Flex className='card-box package-information__hotels-card' vertical>
                <h3 className='package-information__description-title'>Hoteles</h3>
                <PackageHotelsInfo
                  {...configuration.package}
                  hotelInformation={configuration.hotelInformation}
                />
              </Flex>
              <Flex className='card-box package-information__flights-card' vertical>
                <h3 className='package-information__description-title'>Vuelos</h3>
                <PackageDetailFlightsInfo configuration={configuration} />
              </Flex>
              {!!information?.trim()?.length && (
                <Flex className='card-box package-information__description-card' vertical>
                  {information}
                </Flex>
              )}
            </Flex>
            <Flex className='boxes-wrapper package-details' vertical>
              <Flex className='package-details-cards' vertical>
                <Flex className='card-box package-details__summary-card' vertical>
                  <Flex className='package-details__summary-card__header with-bottom-divider'>
                    <h4 className='package-information__summary-title'>
                      {t('packages.details.package_detail')}
                    </h4>
                  </Flex>
                  {packageRefNumber ? (
                    <div className='package-details__ref-number'>
                      <span>Nro. Ref.: {packageRefNumber}</span>
                      <IconButton
                        onClick={() => {
                          navigator.clipboard.writeText(packageRefNumber);
                          alert(t('common.link_copied'));
                        }}
                        sx={{
                          width: 24,
                          height: 24,
                          padding: '4px',
                          marginLeft: '8px',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </div>
                  ) : null}
                  <Flex
                    className='package-details__summary-card__content with-bottom-divider'
                    vertical
                  >
                    <p style={{ fontWeight: 500 }}>
                      {configuration.package.packageDestinations?.[0]?.destinationCity.nombreCiudad}
                    </p>
                    <Flex vertical>
                      <p>
                        Desde{' '}
                        {configuration.package.packageOrigins[0].originExporta.nombreCiudad.trim()}
                      </p>
                      <p>
                        Salida{' '}
                        {formatWithMomentFormat(
                          utcToLocal(
                            configuration.flight.tripInformation.find(({ order }) => order === 1)
                              .departureDate
                          ),
                          DATE_KEYS.FORMAT.DAY_SHORT
                        )}
                      </p>
                      <p style={{ fontWeight: 500 }}>
                        {getAdultsQtyText(configuration.hotelInformation[0].occupation)}
                      </p>
                    </Flex>
                  </Flex>
                  <Flex className='package-details__summary-card__footer' vertical>
                    <DynamicIncludesRow paquetesIncluidos={configuration.package.paquetesIncluidos || []} />
                  </Flex>
                </Flex>
                <Flex className='card-box package-details__payment-card' vertical>
                  <Flex className='package-details__payment-card__header with-bottom-divider'>
                    <p>Precio Total por persona</p>
                    <p
                      style={{
                        fontWeight: 600,
                        fontSize: '1.125rem',
                      }}
                    >
                      {getFinalPrice({
                        currencyCode: configuration.package.currencyCode || 'USD',
                        total: configuration.totalManager?.total,
                      })}
                    </p>
                  </Flex>
                  <Flex
                    className='package-details__payment-card__content with-bottom-divider'
                    vertical
                  >
                    <PaymentSummaryRow
                      label='Precio del producto'
                      value={getFinalPrice({
                        currencyCode: configuration.package.currencyCode || 'USD',
                        total: configuration.totalManager?.subTotal,
                      })}
                    />
                    <PaymentSummaryRow
                      label='Impuestos y tasas'
                      value={getFinalPrice({
                        currencyCode: configuration.package.currencyCode || 'USD',
                        total: configuration.totalManager?.taxTotal,
                      })}
                    />
                  </Flex>
                  {paisId === 1 && (
                    <div className='package-details__payment-card__notice'>
                      <div className='title'>Nota</div>
                      <div className='content'>
                        PARA TARIFAS EXPRESADAS EN DÓLAR ESTADOUNIDENSE (USD). EN CASO DE ABONAR EN
                        PESOS (ARS) SE APLICARÁ EL TIPO DE CAMBIO CORRESPONDIENTE AL DÍA DE PAGO, Y SE
                        ADICIONARÁ LA PERCEPCIÓN CORRESPONDIENTE (RG 4815).
                      </div>
                    </div>
                  )}
                </Flex>
              </Flex>
              <ContactIconsGroup
                productName={configuration?.package?.title}
                productRefNumber={packageRefNumber}
                onEmailClick={() => {
                  setConsultaPackage({
                    ...configuration.package,
                    hotelInformation: configuration.hotelInformation,
                    total: getFinalPrice({
                      currencyCode: configuration.package.currencyCode || 'USD',
                      total: configuration.totalManager?.total,
                    }),
                    providerName: configuration.package.provider.name,
                    link: urlIntranet,
                  });
                }}
              />
            </Flex>
          </Flex>
          <ContactFormModal
            consultaPackage={consultaPackage}
            onClose={() => {
              setConsultaPackage(undefined);
            }}
            variant='package'
          />
        </Flex>
      ) : (
        <Flex
          style={{
            flex: 1,
            height: '30rem',
            justifyContent: 'center',
            padding: '8rem 0',
          }}
        >
          <LoadSpinner />
        </Flex>
      )}
    </>
  );
};

export default PackageDetail;
